import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, doc, setDoc, arrayUnion, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { TrafficTicket, TicketStatus } from '../types';
import { FileText, DollarSign, CheckCircle, AlertCircle, Search } from 'lucide-react';
import PaymentReminderWidget from './PaymentReminderWidget';

const ViewerDashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<TrafficTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [ticketCode, setTicketCode] = useState('');
  const [searchError, setSearchError] = useState('');

  // Calculate days remaining for a ticket (default 18 days from creation)
  const getDaysRemaining = (ticket: TrafficTicket): number => {
    const dueDate = new Date(ticket.createdAt);
    dueDate.setDate(dueDate.getDate() + 18);
    const now = new Date();
    const diffTime = dueDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Format days remaining for display
  const formatDaysRemaining = (days: number): string => {
    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} left`;
    } else if (days === 0) {
      return 'Due today';
    } else {
      return `${Math.abs(days)} day${Math.abs(days) > 1 ? 's' : ''} overdue`;
    }
  };

  // Get color class based on days remaining
  const getDaysRemainingColor = (days: number): string => {
    if (days > 7) return 'text-green-600';
    if (days > 3) return 'text-yellow-600';
    if (days >= 0) return 'text-orange-600';
    return 'text-red-600';
  };

  useEffect(() => {
    fetchViewerTickets();
  }, [currentUser]);

  const fetchViewerTickets = async () => {
    if (!currentUser?.email) return;
    
    setLoading(true);
    try {
      // First, try to fetch tickets where viewerEmail matches current user
      const directTicketsQuery = query(
        collection(db, 'trafficTickets'),
        where('viewerEmail', '==', currentUser.email)
      );
      
      const directTicketsSnap = await getDocs(directTicketsQuery);
      const directTickets: TrafficTicket[] = directTicketsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        paymentDate: doc.data().paymentDate?.toDate(),
        violatorDOB: doc.data().violatorDOB?.toDate(),
        offenceTime: doc.data().offenceTime?.toDate(),
      } as TrafficTicket));
      
      console.log('🎫 Found direct tickets:', directTickets.length);
      console.log('🎫 Direct tickets data:', directTickets.map(t => ({ id: t.id, status: t.status, ticketCode: t.ticketCode })));
      
      // Also fetch from viewerAccounts for backward compatibility
      const viewerAccountRef = doc(db, 'viewerAccounts', currentUser.email);
      const viewerAccountDoc = await getDoc(viewerAccountRef);

      var linkedTickets: TrafficTicket[] = [];
      
      if (viewerAccountDoc.exists()) {
        const linkedCodes = viewerAccountDoc.data().linkedTicketCodes || [];
        
        if (linkedCodes.length > 0) {
          // Create both formats for each code (with and without TKT- prefix)
          var allQueryCodes: string[] = [];
          for (const code of linkedCodes) {
            allQueryCodes.push(code);
            if (!code.startsWith('TKT-') && code.match(/^\d{4}-/)) {
              allQueryCodes.push('TKT-' + code);
            }
          }
          
          // Firestore 'in' queries are limited to 10 items, so we batch if needed
          
          for (let i = 0; i < allQueryCodes.length; i += 10) {
            const batch = allQueryCodes.slice(i, i + 10);
            const ticketsQuery = query(
              collection(db, 'trafficTickets'),
              where('ticketCode', 'in', batch)
            );
            
            const ticketsSnap = await getDocs(ticketsQuery);
            const batchTickets: TrafficTicket[] = ticketsSnap.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate() || new Date(),
              paymentDate: doc.data().paymentDate?.toDate(),
              violatorDOB: doc.data().violatorDOB?.toDate(),
              offenceTime: doc.data().offenceTime?.toDate(),
            } as TrafficTicket));
            
            linkedTickets.push(...batchTickets);
          }
        }
      }
      
      // Combine both sets of tickets and remove duplicates
      const allTickets = [...directTickets, ...linkedTickets];
      const uniqueTickets = allTickets.filter((ticket, index, self) => 
        index === self.findIndex(t => t.id === ticket.id)
      );
      
      console.log('🎫 Total unique tickets:', uniqueTickets.length);
      console.log('🎫 All tickets data:', uniqueTickets.map(t => ({ id: t.id, status: t.status, ticketCode: t.ticketCode })));
      console.log('🎫 Paid tickets count:', uniqueTickets.filter(t => t.status === TicketStatus.PAID).length);
      console.log('🎫 Unpaid tickets count:', uniqueTickets.filter(t => t.status === TicketStatus.ISSUED).length);
      setTickets(uniqueTickets.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()));
      
    } catch (error) {
      console.error('Error fetching viewer tickets:', error);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTicket = async () => {
    if (!ticketCode.trim()) {
      setSearchError('Please enter a ticket code');
      return;
    }

    setSearchError('');
    setLoading(true);

    try {
      let searchCode = ticketCode.trim();
      
      // Auto-add TKT- prefix if not present and code looks like year-format (e.g., 2024-ABC123)
      if (!searchCode.startsWith('TKT-') && /^\d{4}-/.test(searchCode)) {
        searchCode = `TKT-${searchCode}`;
      }
      
      // Try with the formatted code first
      let ticketsQuery = query(
        collection(db, 'trafficTickets'),
        where('ticketCode', '==', searchCode)
      );
      
      let ticketsSnap = await getDocs(ticketsQuery);
      
      // If not found and we added TKT-, try without it
      if (ticketsSnap.empty && searchCode.startsWith('TKT-')) {
        const codeWithoutPrefix = searchCode.replace('TKT-', '');
        ticketsQuery = query(
          collection(db, 'trafficTickets'),
          where('ticketCode', '==', codeWithoutPrefix)
        );
        ticketsSnap = await getDocs(ticketsQuery);
      }
      
      if (ticketsSnap.empty) {
        setSearchError('Ticket not found. Please check the code and try again.');
        setLoading(false);
        return;
      }

      const ticket = ticketsSnap.docs[0];
      const actualTicketCode = ticket.data().ticketCode;
      
      // Link ticket to viewer account
      const viewerAccountRef = doc(db, 'viewerAccounts', currentUser?.email || '');
      await setDoc(viewerAccountRef, {
        email: currentUser?.email,
        linkedTicketCodes: arrayUnion(actualTicketCode),
        lastUpdated: new Date()
      }, { merge: true });
      
      setTicketCode('');
      await fetchViewerTickets();
    } catch (error) {
      console.error('Error adding ticket:', error);
      setSearchError('Failed to add ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const totalFines = tickets.reduce((sum, t) => sum + (t.fineAmount || 0), 0);
  const paidTickets = tickets.filter(t => t.status === TicketStatus.PAID).length;
  const unpaidTickets = tickets.filter(t => t.status === TicketStatus.ISSUED).length;

  const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: string | number; color: string }> = ({
    icon,
    label,
    value,
    color,
  }) => (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${color}`}>{icon}</div>
      </div>
    </div>
  );

  if (loading && tickets.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">My Tickets</h1>
        <p className="text-gray-600 mt-1">View and manage your traffic tickets</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Add a Ticket</h2>
        <div className="flex gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={ticketCode}
              onChange={(e) => setTicketCode(e.target.value)}
              placeholder="Enter ticket code (e.g., 2024-ABC123)"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onKeyPress={(e) => e.key === 'Enter' && handleAddTicket()}
            />
            {searchError && <p className="text-red-600 text-sm mt-2">{searchError}</p>}
          </div>
          <button
            onClick={handleAddTicket}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300 flex items-center gap-2"
          >
            <Search size={20} />
            Add Ticket
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={<FileText className="text-blue-600" size={24} />}
          label="Total Tickets"
          value={tickets.length}
          color="bg-blue-50"
        />
        <StatCard
          icon={<DollarSign className="text-orange-600" size={24} />}
          label="Total Fines"
          value={`EC$${totalFines.toLocaleString()}`}
          color="bg-orange-50"
        />
        <StatCard
          icon={<CheckCircle className="text-green-600" size={24} />}
          label="Paid Tickets"
          value={paidTickets}
          color="bg-green-50"
        />
        <StatCard
          icon={<AlertCircle className="text-red-600" size={24} />}
          label="Unpaid Tickets"
          value={unpaidTickets}
          color="bg-red-50"
        />
      </div>

      {/* Your Tickets Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Your Tickets</h2>
        </div>
        {tickets.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No tickets found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Add a ticket using the ticket code provided by the officer.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ticket Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Offence
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fine Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Days Remaining
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tickets.map((ticket) => (
                  <tr 
                    key={ticket.id} 
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                    className="hover:bg-gray-50 cursor-pointer">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {ticket.ticketCode || ticket.id.slice(0, 8)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {ticket.offenceType}
                      {ticket.customOffence && ` - ${ticket.customOffence}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      EC${ticket.fineAmount?.toLocaleString() || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {ticket.status === TicketStatus.ISSUED ? (
                        <span className={`font-medium ${getDaysRemainingColor(getDaysRemaining(ticket))}`}>
                          {formatDaysRemaining(getDaysRemaining(ticket))}
                        </span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          ticket.status === TicketStatus.PAID
                            ? 'bg-green-100 text-green-800'
                            : ticket.status === TicketStatus.WARRANT_ISSUED
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {ticket.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {ticket.status === TicketStatus.ISSUED && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/payment/${ticket.id}`);
                          }}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                        >
                          Pay Now
                        </button>
                      )}
                      {ticket.status === TicketStatus.PAID && (
                        <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                          Paid
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment Reminders Section */}
      <PaymentReminderWidget />
    </div>
  );
};

export default ViewerDashboard;
